import { META_TYPES, META_TYPES_V4, POSITIVE_GREEN_SUPPORTED_KEYS } from "../constants/BillDetails";
import { VALID_RENDERING_DETAILS_TYPE } from "../constants/RenderingDetails";
import {
    FreeDelCommsStyling,
    ICollapsedBillDetails,
    RenderingDetails,
    RenderingDetailsMeta,
    RenderingDetailsSubDetails,
} from "@swiggy-private/cart-types";
import { isEmpty } from "../helpers/Utils";
import { CSSProperties } from "react";

const RENDERING_DETAILS_KEYS = {
    BILL_HEADING: "bill_heading",
} as const;

export const getBillDetails = (renderingDetails: RenderingDetails[]): RenderingDetails | undefined => {
    return renderingDetails?.find(
        (detailsList) => detailsList?.key?.toLowerCase() === RENDERING_DETAILS_KEYS.BILL_HEADING,
    );
};
export const getCollapsableBillDetailsA11yText = (
    collapsedBillDetails?: ICollapsedBillDetails,
    getSubText?: string,
): string => {
    return `${collapsedBillDetails?.title} ${
        collapsedBillDetails?.totalAmount ? collapsedBillDetails?.totalAmount : ""
    } ${getSubText ? getSubText : ""}`;
};

export const getCollpasableBillDetailsAnalyticsPayload = (
    collapsedBillDetails?: ICollapsedBillDetails,
    expanded?: boolean,
    savedMsg?: string,
    tag?: string,
) => {
    return {
        toPay: collapsedBillDetails?.totalAmount,
        strikeValue: collapsedBillDetails?.strikeoffAmount,
        totalSaved: collapsedBillDetails?.savingsAmount,
        message: collapsedBillDetails?.upsellMessage,
        currentState: expanded ? "uncollapsed" : "collapsed",
        surgeApplied: collapsedBillDetails?.surgeApplied,
        ...(!isEmpty(savedMsg) ? { savedMsg } : {}),
        ...(!isEmpty(tag) ? { tag } : {}),
    };
};

export const getInfoTextFontStyles = (
    { meta = {} }: RenderingDetailsMeta,
    freeDelCommsStyles: FreeDelCommsStyling = {},
): CSSProperties => {
    const { infoTextColor } = meta || {};
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { infoTextColor: phInfoTextColor } = freeDelCommsStyles;
    const infoColor = !isEmpty(infoTextColor) ? infoTextColor : undefined;
    const phInfoColor = !isEmpty(phInfoTextColor) ? phInfoTextColor : undefined;
    return {
        color: infoColor || phInfoColor,
    };
};

// TODO: to update to generic contract post release
export const getPositiveColorText = (text: string, itemKey: string): string => {
    if (!POSITIVE_GREEN_SUPPORTED_KEYS.includes(itemKey?.toLowerCase())) return text;

    return text.replace("[[", `<span style="color: #1ba672;">`).replace("]]", "</span>");
};

export const canIntermediateTextStrikeoff = ({ meta = {} }: RenderingDetailsMeta): boolean =>
    meta?.intermediateTextStriked ?? false;

export const getIntermediateTextFontStyles = ({ meta = {} }: RenderingDetailsMeta): CSSProperties => {
    const { intermediateTextColor } = meta || {};
    return {
        color: !isEmpty(intermediateTextColor) ? intermediateTextColor : undefined,
    };
};

export const canValueTextStrikeoff = ({ meta = {} }: RenderingDetailsMeta): boolean => meta?.valueTextStriked ?? false;

export const getValueTextFontStyles = ({ meta = {} }: RenderingDetailsMeta): CSSProperties => {
    const { valueTextStyle, valueTextColor } = meta || {};
    return {
        color: !isEmpty(valueTextColor) ? valueTextColor : "#02060cbf",
        fontWeight: valueTextStyle === "bold" ? 600 : 200,
    };
};

export const isBillItemMetaDatatypeIsString = ({ meta = {} }: RenderingDetailsMeta): boolean => {
    const { dataType } = meta || {};
    return dataType === META_TYPES.DATATYPE.STRING;
};

export const canShowTooltip = (billDetail: RenderingDetails): boolean => {
    if (isEmpty(billDetail) || !billDetail.type) {
        return false;
    }
    const itemType = billDetail.type.toUpperCase();
    if (itemType === VALID_RENDERING_DETAILS_TYPE.DISPLAY) {
        return !isEmpty(billDetail.info_text);
    } else if (itemType === VALID_RENDERING_DETAILS_TYPE.TOOLTIPDISPLAY) {
        return !isEmpty(billDetail.sub_details);
    }
    return false;
};

export const getBillItemFontStyle = ({ meta = {} }: RenderingDetails): CSSProperties => {
    const { color, bold } = meta || {};
    return {
        color: !isEmpty(color) ? color : undefined,
        fontWeight: !isEmpty(bold) ? 600 : undefined,
    };
};

export const getBillItemMetaWindowSizeV4 = ({ meta = {} }: RenderingDetailsMeta): string => {
    const { windowWidth } = meta || {};
    let windowSize;
    if (!isEmpty(windowWidth)) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        windowSize = META_TYPES_V4.WINDOW_WIDTH[windowWidth.toUpperCase()];
    }
    return windowSize || META_TYPES_V4.WINDOW_WIDTH.XXL;
};

/**
 * Helper function to format sub-detail value by combining intermediateText and value
 * @param subDetail - The sub-detail object containing intermediateText and value
 * @returns Formatted string combining intermediateText and value
 */
const getFormattedSubDetailValue = (subDetail: RenderingDetailsSubDetails): string => {
    return (subDetail?.intermediateText || "") + " " + (subDetail?.value || "");
};

/**
 * Flattens a nested structure of sub-details into a single key-value object.
 * Combines intermediateText and value for each sub-detail with a key.
 */
export const getFlattenedSubDetails = (subDetails: RenderingDetailsSubDetails[]): Record<string, string> => {
    const result: Record<string, string> = {};

    subDetails?.forEach((subDetail) => {
        if (subDetail?.key) {
            result[subDetail.key] = getFormattedSubDetailValue(subDetail);
        }

        subDetail?.sub_details?.forEach((nested) => {
            if (nested?.key) {
                result[nested.key] = getFormattedSubDetailValue(nested);
            }
        });
    });

    return result;
};
